import React from 'react';

import { getPrivacyProfile } from '../config/privacy';
import { fetchDisplayProfile } from '../services/displayProfile';

const PROFILE_REFRESH_MS = 5 * 60 * 1000;
const PROFILE_TIMEOUT_MS = 8000;

export default function useDisplayProfile() {
  const [profile, setProfile] = React.useState(() => getPrivacyProfile(false));
  const [resolved, setResolved] = React.useState(false);
  const requestRef = React.useRef(null);

  React.useEffect(() => {
    let mounted = true;

    const refresh = () => {
      if (requestRef.current) return requestRef.current.promise;

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), PROFILE_TIMEOUT_MS);
      const promise = fetchDisplayProfile({ signal: controller.signal })
        .then((nextProfile) => {
          if (mounted) {
            setProfile((currentProfile) =>
              currentProfile.mode === nextProfile.mode ? currentProfile : nextProfile
            );
            setResolved(true);
          }
          return nextProfile;
        })
        .finally(() => {
          window.clearTimeout(timeoutId);
          if (requestRef.current?.controller === controller) {
            requestRef.current = null;
          }
        });

      requestRef.current = { controller, promise };
      return promise;
    };

    refresh();
    const intervalId = window.setInterval(refresh, PROFILE_REFRESH_MS);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      requestRef.current?.controller.abort();
      requestRef.current = null;
    };
  }, []);

  return { profile, resolved };
}
